import React, { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { LogOut, Menu, X, ChevronDown, UserCircle, Building2, Handshake, LineChart, Briefcase } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { NAV_ITEMS } from "@/config/navigation";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/ds";

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { user, organization, logout, isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const initials = `${user && typeof user === "object" ? user.first_name?.[0] ?? "" : ""}${
    user && typeof user === "object" ? user.last_name?.[0] ?? "" : ""
  }`.toUpperCase() || "U";

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-slate-200 px-6">
        <BrandLogo className="h-8" />
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-3 py-4">
        {NAV_ITEMS.map(({ key, path, label, icon: Icon }) => (
          <NavLink
            key={key}
            to={path}
            onClick={onNavigate}
            data-testid={`nav-${key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={2} />
            {label}
          </NavLink>
        ))}
        {isPlatformAdmin && (
          <>
            <div className="mt-4 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">Platform Admin</div>
            <NavLink
              to="/app/admin/agency"
              onClick={onNavigate}
              data-testid="nav-admin-agency"
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Handshake className="h-[18px] w-[18px]" strokeWidth={2} />
              Agency Partners
            </NavLink>
          </>
        )}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="mb-2 flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">{initials}</div>
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-slate-900">{organization?.name}</p>
            <p className="truncate text-[11px] text-slate-500">{organization?.industry}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={async () => { await logout(); navigate("/login"); }}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
        >
          <LogOut className="h-[18px] w-[18px]" strokeWidth={2} />
          Sign out
        </button>
      </div>
    </div>
  );
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const displayUser = user && typeof user === "object" ? user : null;
  const initials = `${displayUser?.first_name?.[0] ?? ""}${displayUser?.last_name?.[0] ?? ""}`.toUpperCase() || "U";

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-200 bg-white lg:block">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-nav">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-white shadow-lg">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-8">
          <button
            data-testid="mobile-menu-btn"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="hidden sm:block">
            <StatusBadge tone="blue">Version 1 · Foundation</StatusBadge>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                data-testid="account-menu-btn"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-100"
              >
                {displayUser && (
                  <span className="hidden text-right sm:block">
                    <span className="block text-sm font-medium text-slate-900">{displayUser.email}</span>
                  </span>
                )}
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white">{initials}</span>
                <ChevronDown className="h-4 w-4 text-slate-400" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg" data-testid="account-menu">
                    <Link to="/app/settings" onClick={() => setMenuOpen(false)} data-testid="menu-profile" className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                      <UserCircle className="h-4 w-4" /> Profile settings
                    </Link>
                    <Link to="/app/settings" onClick={() => setMenuOpen(false)} data-testid="menu-org" className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                      <Building2 className="h-4 w-4" /> Organization settings
                    </Link>
                    {["senior_access_admin", "platform_admin", "onboarding_staff", "sales_rep"].includes(
                      (displayUser as { platform_role?: string })?.platform_role ?? "") && (
                      <Link to="/internal" onClick={() => setMenuOpen(false)} data-testid="menu-staff-portal" className="flex items-center gap-2.5 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50">
                        <Briefcase className="h-4 w-4" /> Staff portal
                      </Link>
                    )}
                    {(displayUser as { platform_role?: string })?.platform_role === "senior_access_admin" && (
                      <Link to="/executive/sales-team" onClick={() => setMenuOpen(false)} data-testid="menu-executive" className="flex items-center gap-2.5 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50">
                        <LineChart className="h-4 w-4" /> Executive Command Center
                      </Link>
                    )}
                    <div className="my-1 border-t border-slate-100" />
                    <button data-testid="menu-logout" onClick={async () => { await logout(); navigate("/login"); }} className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                      <LogOut className="h-4 w-4" /> Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>
        <main className="p-4 sm:p-8">{children}</main>
      </div>
    </div>
  );
}
